import React, {useCallback, useEffect, useMemo, useState} from 'react'
import useTranslate from "../../../modules/local/useTranslate";
import classNames from "classnames";
import {FiEdit3} from "react-icons/fi";
import {Button} from "antd";
import {FaRegTrashCan} from "react-icons/fa6";


// Thang điểm chữ cơ sở và các ký hiệu bổ sung
const BASE_GRADES = ['A', 'B', 'C', 'D', 'F'];
const MODIFIERS = [
    {display: '--', suffix: '--'}, // Double Minus
    {display: '-', suffix: '-'},    // Minus
    {display: '', suffix: ''}, // Even/Plain
    {display: '+', suffix: '+'},    // Plus
    {display: '++', suffix: '++'},  // Double Plus (Tương đương +1/2)
];

/**
 * Utility function to convert a grade string (e.g., "A-", "B++", "F") back to its base and modifier.
 * @param {string | null} gradeString
 * @returns {{base: string | null, modifierSuffix: string | null}}
 */
const gradeToParts = (gradeString) => {
    if (!gradeString) return {base: null, modifierSuffix: null};

    // Find the longest modifier suffix first to correctly parse grades like "A++" (length 2) before "A+" (length 1)
    const longestModifier = MODIFIERS.map(m => m.suffix).filter(s => s !== '').sort((a, b) => b.length - a.length);

    for (const suffix of longestModifier) {
        if (gradeString.endsWith(suffix) && gradeString.length > suffix.length) {
            const base = gradeString.substring(0, gradeString.length - suffix.length);
            if (BASE_GRADES.includes(base)) {
                return {base, modifierSuffix: suffix};
            }
        }
    }
    // If no modifier is found, the grade string is the base grade (e.g., "A", "F")
    if (BASE_GRADES.includes(gradeString)) {
        return {base: gradeString, modifierSuffix: ''};
    }
    return {base: null, modifierSuffix: null};
};

// --- Custom Component: GradeLetterInput ---

/**
 * Custom input component for entering letter grades with modifiers (e.g., A++, B-).
 * Behaves like a controlled input via value and onChange props.
 * @param {{value: string | null, onChange: (value: string | null) => void}} props
 */
const GradeLetterInput = ({defaultValue, onChange}) => {
    const t = useTranslate();
    const [isSelectingBase, setIsSelectingBase] = useState(true);
    const [tempBase, setTempBase] = useState(null); // Stores the selected base letter (A, B, C, ...)

    // Derived state: Parse the incoming grade string
    const {base, modifierSuffix} = useMemo(() => gradeToParts(defaultValue), [defaultValue]);

    // Synchronize local state (tempBase) with external prop value
    useEffect(() => {
        if (!defaultValue) {
            setTempBase(null);
            setIsSelectingBase(true);
        } else {
            setTempBase(base);
        }
    }, [defaultValue, base]);

    const handleBaseSelect = useCallback((selectedBase) => {
        setTempBase(selectedBase);
        setIsSelectingBase(false);

        // If a base is selected, apply the current modifier (if it exists) or default to 'Chẵn' ('')
        const currentModifier = modifierSuffix || '';
        onChange(selectedBase + currentModifier);
    }, [onChange, modifierSuffix]);

    const handleModifierSelect = useCallback((suffix) => {
        if (!tempBase) return; // Cannot select modifier without a base

        const newGrade = tempBase + suffix;
        onChange(newGrade);
        setIsSelectingBase(false); // Stay in modifier view until reset/edit
    }, [tempBase, onChange]);

    const handleClear = useCallback(() => {
        onChange(null);
        setTempBase(null);
        setIsSelectingBase(true);
    }, [onChange]);

    const handleEdit = useCallback(() => {
        setIsSelectingBase(true);
    }, []);

    const DisplayGrade = (
        <div className="flex items-center justify-between p-4 border border-color-50 shadow-md rounded-xl transition duration-150">
            <div className="flex flex-col">
                <span className="text-sm text-color-300 tracking-wider">{t("Selected Grade")}</span>
                <span className="text-3xl font-bold text-color-000">
                    {defaultValue === null ? '—' : defaultValue}
                </span>
            </div>
            <div className="flex space-x-2">
                {defaultValue !== null && (
                    <Button
                        color="default" variant="filled"
                        onClick={handleEdit}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 shadow-md rounded-md no-border transition duration-150"
                        title={t("edit grade")}
                        icon={<FiEdit3 />}
                    />
                )}
                <Button
                    color="primary" variant="filled"
                    onClick={handleClear}
                    className="p-2 bg-primary-50 hover:bg-primary-100 text-primary-600 shadow-md rounded-md no-border transition duration-150"
                    title={t("clear grade")}
                    icon={<FaRegTrashCan />}
                />
            </div>
        </div>
    );

    const BaseGradeSelector = (
        <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100">
            <h3 className="text-base font-semibold text-gray-700 mb-3">
                1. {t("Select Base Grade")} ({t("Letter Grade")})
            </h3>
            <div className="grid grid-cols-5 gap-3">
                {BASE_GRADES.map((g) => {
                    const isSelected = tempBase === g;
                    return (
                        <button
                            key={g}
                            onClick={() => handleBaseSelect(g)}
                            className={classNames(
                                "flex-1 py-3 text-center text-sm font-semibold shadow rounded-lg transition-all duration-200 active:scale-95",
                                isSelected ?
                                    'bg-indigo-500 text-white' :
                                    'background text-color-200 hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-200'
                            )}>
                            {g}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const ModifierSelector = (
        <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100 mt-4">
            <h3 className="text-base font-semibold text-gray-700 mb-3">
                2. {t("Select Modifier")} ({t("Modifier")})
            </h3>
            <div className="flex justify-between gap-x-3">
                {MODIFIERS.map((m) => {
                    // Check if the current modifier matches the suffix of the selected grade
                    const isSelected = m.suffix === modifierSuffix && tempBase === base;
                    return (
                        <button
                            key={m.suffix}
                            onClick={() => handleModifierSelect(m.suffix)}
                            className={classNames(
                                "flex-1 py-3 text-center text-sm font-semibold shadow rounded-lg transition-all duration-200 active:scale-95 shadow-md",
                                isSelected
                                    ? 'bg-indigo-500 text-white'
                                    : 'background text-color-200 hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-200'
                            )}>
                            {`${tempBase} ${m.display}`}
                        </button>
                    );
                })}
            </div>
            <div
                onClick={handleEdit}
                className="mt-4 py-2 text-center text-sm font-medium text-indigo-500 hover:text-primary transition active:scale-95 cursor-pointer">
                {t("Edit Base Grade")} ({tempBase})
            </div>
        </div>
    );

    return (
        <div className="w-full space-y-4">
            {DisplayGrade}
            {isSelectingBase || tempBase === null
                ? BaseGradeSelector
                : ModifierSelector
            }
        </div>
    );
};

export default GradeLetterInput
